/**
 * Every Robotensor service answers errors as RFC 9457 problem+json with a stable `code`; clients
 * branch on `code`, never on `detail` text. `detail` is already written for people.
 */

export interface ProblemField {
  loc: string[]
  msg: string
}

export interface Problem {
  type?: string
  status?: number
  code?: string
  detail?: string
  request_id?: string
  fields?: ProblemField[]
  [extra: string]: unknown
}

/** Client-side codes for failures that never reached a service handler. */
export const CLIENT_CODES = {
  network: 'network',
  /** The route doesn't exist on this server yet (pending module/service). */
  unavailable: 'endpoint_unavailable',
  /** The gateway/proxy couldn't reach the service. */
  unreachable: 'service_unreachable',
  aborted: 'aborted',
  badResponse: 'bad_response',
} as const

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string
  readonly requestId: string | null
  readonly fields: ProblemField[]
  readonly problem: Problem | null
  readonly method: string
  readonly path: string

  constructor(init: { status: number; code: string; detail: string; requestId?: string | null; fields?: ProblemField[]; problem?: Problem | null; method: string; path: string }) {
    super(init.detail || init.code)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.detail = init.detail
    this.requestId = init.requestId ?? null
    this.fields = init.fields ?? []
    this.problem = init.problem ?? null
    this.method = init.method
    this.path = init.path
  }

  /** The surface exists in the product but its backend isn't live (404 route, unreachable service). */
  get unavailable(): boolean {
    return this.code === CLIENT_CODES.unavailable || this.code === CLIENT_CODES.unreachable
  }

  get isAuth(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404 && !this.unavailable
  }

  /** `GET /api/platform/v1/library` — for honest "not connected yet" states. */
  get endpoint(): string {
    return `${this.method} ${this.path.split('?')[0]}`
  }

  /** First validation message for a field name (`email`, `code`, …). */
  fieldError(name: string): string | undefined {
    return this.fields.find((f) => f.loc[f.loc.length - 1] === name)?.msg
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

const FALLBACK: Record<string, string> = {
  network: 'Couldn’t reach Robotensor. Check your connection; we’ll retry when you’re back.',
  endpoint_unavailable: 'This part of the platform isn’t live yet.',
  service_unreachable: 'This service isn’t reachable right now.',
  bad_response: 'The server sent a response we couldn’t read.',
  rate_limited: 'Too many requests. Wait a moment and try again.',
  forbidden: 'You don’t have access to this.',
  signin_required: 'Please sign in to continue.',
}

/** A sentence for the person: the server's own detail when it has one, else a plain fallback. */
export function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.detail && e.code !== CLIENT_CODES.unavailable && e.code !== CLIENT_CODES.unreachable) return e.detail
    return FALLBACK[e.code] ?? (e.detail || 'Something went wrong. Try again.')
  }
  if (e instanceof Error) return e.message
  return 'Something went wrong. Try again.'
}
